import {
	endpointsMap,
	ICheckIfCustomerExistsOptions,
	ICheckIfCustomerExistsResponse,
	IResetCustomerPasswordOptions,
	IResetCustomerPasswordResponse,
	ICheckResetTokenValidityOptions,
	ICheckResetTokenValidityResponse,
	ISetNewCustomerPasswordOptions,
	ISetNewCustomerPasswordResponse,
	ILoginOnBehalfOptions,
	ILoginOnBehalfResponse,
} from './ICustomShopperCustomer';

import AbstractCustomShopperAPI from '../AbstractCustomShopperAPI';
import {ICustomApiConfig} from '../ICustomApiConfig';

/**
 * Represents CustomShopperCustomer
 */
class CustomShopperCustomer extends AbstractCustomShopperAPI {
	/**
	 * @constructor
	 * @param {ICustomApiConfig} config - API Config
	 */
	constructor(config: ICustomApiConfig) {
		super(config);
	}

	/**
	 * Check if customer exists by email
	 * @param {ICheckIfCustomerExistsOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {ICheckIfCustomerExistsResponse} - Customer exists response
	 */
	public async checkIfCustomerExists(
		fetchOptions: ICheckIfCustomerExistsOptions,
		throwOnError?: boolean): Promise<ICheckIfCustomerExistsResponse> {
		const response: ICheckIfCustomerExistsResponse = await this.callCustomEndpoint<ICheckIfCustomerExistsResponse>(
			fetchOptions,
			endpointsMap.CUSTOMER_EXISTS
		);

		if (throwOnError && !response.existingStatus) {
			const error = new Error(response.detail || 'Customer exists failed');
			error.name = response.title || 'Customer exists error';

			throw error;
		}

		return response;
	}

	/**
	 * Reset customer password
	 * @param {IResetCustomerPasswordOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {IResetCustomerPasswordResponse} - Reset customer password response
	 */
	public async resetCustomerPassword(
		fetchOptions: IResetCustomerPasswordOptions,
		throwOnError?: boolean): Promise<IResetCustomerPasswordResponse> {
		const response: IResetCustomerPasswordResponse = await this.callCustomEndpoint<IResetCustomerPasswordResponse>(
			fetchOptions,
			endpointsMap.RESET_CUSTOMER_PASSWORD
		);

		if (throwOnError && !response.message) {
			const error = new Error(response.detail || 'Reset customer password failed');
			error.name = response.title || 'Reset customer password error';

			throw error;
		}

		return response;
	}

	/**
	 * Check reset customer password token validity
	 * @param {ICheckResetTokenValidityOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {ICheckResetTokenValidityResponse} - Check reset token validity response
	 */
	public async checkResetTokenValidity(
		fetchOptions: ICheckResetTokenValidityOptions,
		throwOnError?: boolean): Promise<ICheckResetTokenValidityResponse> {
		const response: ICheckResetTokenValidityResponse = await this.callCustomEndpoint<ICheckResetTokenValidityResponse>(
			fetchOptions,
			endpointsMap.CHECK_TOKEN_VALIDITY
		);

		if (throwOnError && !response.isValid) {
			const error = new Error(response.detail || 'Check reset token validity failed');
			error.name = response.title || 'Check reset token validity error';

			throw error;
		}

		return response;
	}

	/**
	 * Set new customer password
	 * @param {ICheckResetTokenValidityOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {ISetNewCustomerPasswordResponse} - Set new customer password response
	 */
	public async setNewCustomerPassword(
		fetchOptions: ISetNewCustomerPasswordOptions,
		throwOnError?: boolean): Promise<ISetNewCustomerPasswordResponse> {
		const response: ISetNewCustomerPasswordResponse = await this.callCustomEndpoint<ISetNewCustomerPasswordResponse>(
			fetchOptions,
			endpointsMap.SET_NEW_CUSTOMER_PASSWORD
		);

		if (throwOnError && !response.message) {
			const error = new Error(response.detail || 'Set new customer password failed');
			error.name = response.title || 'Set new customer password error';

			throw error;
		}

		return response;
	}

	/**
	 * Login on behalf of customer
	 * @param {ICheckResetTokenValidityOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {ILoginOnBehalfResponse} - Login on behalf response
	 */
	public async loginOnBehalf(
		fetchOptions: ILoginOnBehalfOptions,
		throwOnError?: boolean): Promise<ILoginOnBehalfResponse> {
		const response: ILoginOnBehalfResponse = await this.callCustomEndpoint<ILoginOnBehalfResponse>(
			fetchOptions,
			endpointsMap.LOGIN_ON_BEHALF
		);

		if (throwOnError && !response?.result?.access_token) {
			const error = new Error(response.detail || 'Login on behalf failed');
			error.name = response.title || 'Login on behalf error';

			throw error;
		}

		return response;
	}
}

export default CustomShopperCustomer;
